import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { 
    AlertTriangle, Clock, Wrench, Car, X, ChevronRight, 
    AlertCircle, Calendar, Gauge, Settings, Check
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useNavigate } from 'react-router-dom';

export default function MaintenanceAlertsModal({ 
    open, 
    onOpenChange, 
    language,
    onSettingsClick
}) {
    const [loading, setLoading] = useState(true);
    const [alertsData, setAlertsData] = useState(null);
    const [dismissing, setDismissing] = useState(null);
    const navigate = useNavigate();

    const fetchAlerts = async () => {
        setLoading(true);
        try {
            const response = await api.get('/maintenance-alerts');
            setAlertsData(response.data);
        } catch (error) {
            console.error('Failed to fetch alerts:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchAlerts();
        }
    }, [open]);

    const handleDismiss = async (maintenanceId) => {
        setDismissing(maintenanceId);
        try {
            await api.post(`/maintenance-alerts/dismiss/${maintenanceId}`);
            // Remove from local state
            setAlertsData(prev => ({
                ...prev,
                alerts: prev.alerts.filter(a => a.id !== maintenanceId)
            }));
            toast.success(language === 'el' ? 'Η ειδοποίηση απορρίφθηκε' : 'Alert dismissed');
        } catch (error) {
            toast.error(language === 'el' ? 'Αποτυχία' : 'Failed');
        } finally {
            setDismissing(null);
        }
    };

    const handleViewMaintenance = () => {
        onOpenChange(false);
        navigate('/app/maintenance');
    };

    const getUrgencyStyle = (urgency) => {
        switch (urgency) {
            case 'overdue':
                return 'bg-red-50 border-red-200 text-red-800';
            case 'urgent':
                return 'bg-amber-50 border-amber-200 text-amber-800';
            default:
                return 'bg-blue-50 border-blue-200 text-blue-800';
        }
    };

    const getUrgencyIcon = (urgency) => {
        switch (urgency) {
            case 'overdue':
                return <AlertCircle className="w-5 h-5 text-red-500" />;
            case 'urgent':
                return <AlertTriangle className="w-5 h-5 text-amber-500" />;
            default:
                return <Clock className="w-5 h-5 text-blue-500" />;
        }
    };

    const getUrgencyLabel = (urgency) => {
        if (language === 'el') {
            switch (urgency) {
                case 'overdue': return 'Εκπρόθεσμο';
                case 'urgent': return 'Επείγον';
                default: return 'Επερχόμενο';
            }
        } else {
            switch (urgency) {
                case 'overdue': return 'Overdue';
                case 'urgent': return 'Urgent';
                default: return 'Upcoming';
            }
        }
    };

    const formatDaysUntil = (days) => {
        if (days === null || days === undefined) return null;
        if (days < 0) {
            const overdue = Math.abs(days);
            return language === 'el' 
                ? `${overdue} ${overdue === 1 ? 'ημέρα' : 'ημέρες'} καθυστέρηση`
                : `${overdue} ${overdue === 1 ? 'day' : 'days'} overdue`;
        }
        if (days === 0) return language === 'el' ? 'Σήμερα' : 'Today';
        if (days === 1) return language === 'el' ? 'Αύριο' : 'Tomorrow';
        return language === 'el' ? `Σε ${days} ημέρες` : `In ${days} days`;
    };

    const formatKmUntil = (km) => {
        if (km === null || km === undefined) return null;
        if (km < 0) {
            return language === 'el' 
                ? `${Math.abs(km).toLocaleString()} km υπέρβαση`
                : `${Math.abs(km).toLocaleString()} km over`;
        }
        return language === 'el' 
            ? `Σε ${km.toLocaleString()} km`
            : `In ${km.toLocaleString()} km`;
    };

    const totalAlerts = alertsData ? 
        alertsData.overdue_count + alertsData.urgent_count + alertsData.upcoming_count : 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg max-h-[85vh] bg-white p-0 flex flex-col overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 flex-shrink-0">
                    <DialogTitle className="text-white flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Wrench className="w-6 h-6" />
                            <span className="text-lg font-semibold">
                                {language === 'el' ? 'Ειδοποιήσεις Συντήρησης' : 'Maintenance Alerts'}
                            </span>
                        </div>
                        {alertsData && (
                            <div className="flex gap-2">
                                {alertsData.overdue_count > 0 && (
                                    <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full">
                                        {alertsData.overdue_count} {language === 'el' ? 'εκπρόθεσμα' : 'overdue'}
                                    </span>
                                )}
                                {alertsData.urgent_count > 0 && (
                                    <span className="bg-amber-600 text-white text-xs px-2 py-1 rounded-full">
                                        {alertsData.urgent_count} {language === 'el' ? 'επείγοντα' : 'urgent'}
                                    </span>
                                )}
                            </div>
                        )}
                    </DialogTitle>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex flex-col">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                        </div>
                    ) : alertsData && alertsData.alerts.length > 0 ? (
                        <div className="flex flex-col flex-1 overflow-hidden p-4">
                            {/* Summary */}
                            <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm text-slate-600 flex-shrink-0">
                                {language === 'el' 
                                    ? `Έχετε ${totalAlerts} ${totalAlerts === 1 ? 'συντήρηση' : 'συντηρήσεις'} που χρειάζονται προσοχή.`
                                    : `You have ${totalAlerts} maintenance ${totalAlerts === 1 ? 'item' : 'items'} that need attention.`}
                                <br />
                                <span className="text-xs text-slate-400">
                                    {language === 'el' 
                                        ? `Ειδοποίηση: ${alertsData.settings.days_before} ημέρες ή ${alertsData.settings.km_before.toLocaleString()} km πριν`
                                        : `Alert threshold: ${alertsData.settings.days_before} days or ${alertsData.settings.km_before.toLocaleString()} km before`}
                                </span>
                            </div>

                            {/* Alerts List with Scrollbar */}
                            <div className="flex-1 overflow-y-auto pr-2 min-h-0" style={{ maxHeight: 'calc(85vh - 280px)' }}>
                                <div className="space-y-3">
                                    {alertsData.alerts.map((alert) => (
                                        <div
                                            key={alert.id}
                                            className={`rounded-lg border p-3 ${getUrgencyStyle(alert.urgency)}`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                                    {getUrgencyIcon(alert.urgency)}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="font-semibold">
                                                                {alert.maintenance_type}
                                                            </span>
                                                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                                                                alert.urgency === 'overdue' ? 'bg-red-200' :
                                                                alert.urgency === 'urgent' ? 'bg-amber-200' : 'bg-blue-200'
                                                            }`}>
                                                                {getUrgencyLabel(alert.urgency)}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1 text-sm mt-1 opacity-80">
                                                            <Car className="w-3.5 h-3.5" />
                                                            <span className="truncate">{alert.vehicle_info}</span>
                                                        </div>
                                                        <div className="flex flex-wrap gap-3 mt-2 text-xs">
                                                            {alert.days_until !== null && (
                                                                <span className="flex items-center gap-1">
                                                                    <Calendar className="w-3.5 h-3.5" />
                                                                    {formatDaysUntil(alert.days_until)}
                                                                </span>
                                                            )}
                                                            {alert.km_until !== null && (
                                                                <span className="flex items-center gap-1">
                                                                    <Gauge className="w-3.5 h-3.5" />
                                                                    {formatKmUntil(alert.km_until)}
                                                                </span>
                                                            )}
                                                            {alert.cost > 0 && (
                                                                <span>~€{alert.cost}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0 opacity-60 hover:opacity-100"
                                                    onClick={() => handleDismiss(alert.id)}
                                                    disabled={dismissing === alert.id}
                                                >
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 mt-4 pt-4 border-t flex-shrink-0">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => onSettingsClick?.()}
                                >
                                    <Settings className="w-4 h-4 mr-2" />
                                    {language === 'el' ? 'Ρυθμίσεις' : 'Settings'}
                                </Button>
                                <Button
                                    className="flex-1 bg-amber-500 hover:bg-amber-400 text-white"
                                    onClick={handleViewMaintenance}
                                >
                                    {language === 'el' ? 'Προβολή Συντήρησης' : 'View Maintenance'}
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 p-4">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Check className="w-8 h-8 text-green-500" />
                            </div>
                            <p className="text-slate-600 font-medium">
                                {language === 'el' 
                                    ? 'Όλα καλά! Δεν υπάρχουν επερχόμενες συντηρήσεις.'
                                    : 'All good! No upcoming maintenance.'}
                            </p>
                            <Button
                                variant="outline"
                                className="mt-4"
                                onClick={() => onOpenChange(false)}
                            >
                                {language === 'el' ? 'Κλείσιμο' : 'Close'}
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
